import { useCallback, useMemo, useState } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { Select, SelectOption, Switch, TextInput } from '@cbhq/cds-web/controls';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextDisplay3, TextLabel1, TextTitle3 } from '@cbhq/cds-web/typography';
import { ProgressBar } from '@cbhq/cds-web/visualizations/ProgressBar';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { usePaymasterContext } from ':cloud/contexts/PaymasterProvider';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useGetPaymasterInfo } from ':cloud/queries/Base/useGetPaymasterInfo';
import { useUpdateGasPolicy } from ':cloud/queries/Base/useUpdateGasPolicy';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { GasPolicy } from ':cloud/types/ts_types';
import { currencyFormatter } from ':cloud/utils/formatters/currency';
import {
  formatTxnAmount,
  getKeyByValue,
  isValidGasAmount,
  isValidTxnAmount,
} from ':cloud/utils/paymaster';

import {
  LIMIT_CYCLE_MAP,
  maxGasErrorMessage,
  maxUserOpGasErrorMessage,
  minGasError,
  minUserOpError,
} from './constants';
import { ContractAllowlistBanner } from './ContractAllowlistBanner';
import { PaymasterGasBanner } from './PaymasterGasBanner';
import { PaymasterLimitHeader } from './PaymasterLimitHeader';

type PaymasterLimitSectionProps = {
  gasPolicy: GasPolicy;
  isTestnet: boolean;
};

export function PaymasterLimitSection({ gasPolicy, isTestnet }: PaymasterLimitSectionProps) {
  const [maxGlobalGasUsd, setMaxGlobalGasUsd] = useState(
    JSON.stringify(gasPolicy?.maxGlobalGasUsd) || '0',
  );
  const [maxGasPerAddrUsd, setMaxGasPerAddrUsd] = useState(
    JSON.stringify(gasPolicy?.maxGasPerAddrUsd) || '0',
  );
  const [maxGasPerUserOpUsd, setMaxGasPerUserOpUsd] = useState(
    JSON.stringify(gasPolicy?.maxGasPerUserOperationUsd) || '0',
  );
  const [maxTxnPerAddr, setMaxTxnPerAddr] = useState(gasPolicy?.maxTxnPerAddr || '0');

  const [isPerUserLimitEnabled, setIsPerUserLimitEnabled] = useState(
    !!gasPolicy?.perAddrMaxEnabled,
  );
  const [isPerUserOpLimitEnabled, setIsPerUserOpLimitEnabled] = useState(
    !!gasPolicy?.maxGasPerUserOperationEnabled,
  );

  const initialPerAddressLimitCycle = useMemo(() => {
    if (gasPolicy?.intervalType && getKeyByValue(LIMIT_CYCLE_MAP, gasPolicy?.intervalType)) {
      return getKeyByValue(LIMIT_CYCLE_MAP, gasPolicy?.intervalType);
    }
    return Object.keys(LIMIT_CYCLE_MAP)[0];
  }, [gasPolicy?.intervalType]);

  const [perAddressLimitCycle, setPerAddressLimitCycle] = useState(initialPerAddressLimitCycle);

  const { setCompletedStep } = usePaymasterContext();

  const { activeOrg } = useGetUser();
  const { selectedProject } = useAppState();
  const { paymasterInfo } = useGetPaymasterInfo({
    organizationId: activeOrg?.organizationId,
    projectId: selectedProject?.id,
    isTestnet,
  });

  const updateGasPolicy = useUpdateGasPolicy({
    organizationId: activeOrg?.organizationId || '',
    projectId: selectedProject?.id || '',
    previousPolicy: gasPolicy,
    onSuccess: (newPolicy: GasPolicy) => {
      setCompletedStep('gas');
      if (!newPolicy.maxGasPerUserOperationEnabled) {
        setMaxGasPerUserOpUsd(JSON.stringify(newPolicy?.maxGasPerUserOperationUsd));
      }
      if (!newPolicy.perAddrMaxEnabled) {
        setMaxGasPerAddrUsd(JSON.stringify(newPolicy?.maxGasPerAddrUsd));
      }
    },
    successMsg: 'Gas policy updated',
  });

  const { isPhone } = useBreakpoints();
  const responsiveTemplateColumns = useMemo(() => {
    return isPhone ? '1fr' : '1fr 1fr';
  }, [isPhone]);

  const sponsoredGas = useMemo(() => {
    return paymasterInfo?.usdSponsored;
  }, [paymasterInfo?.usdSponsored]);

  const progressBarProgress = useMemo(() => {
    if (sponsoredGas === undefined || gasPolicy?.maxGlobalGasUsd === undefined) {
      return 0;
    }
    return sponsoredGas / gasPolicy.maxGlobalGasUsd;
  }, [gasPolicy.maxGlobalGasUsd, sponsoredGas]);

  const isDisabled = !gasPolicy?.contractAllowlist?.length;
  const isApproachingGasLimit = progressBarProgress >= 0.75;

  const formattedSponsoredGas = sponsoredGas !== undefined ? currencyFormatter(sponsoredGas) : '-';
  const formattedMaxGlobalGas =
    gasPolicy?.maxGlobalGasUsd !== undefined ? currencyFormatter(gasPolicy?.maxGlobalGasUsd) : '-';

  const globalMaxGasError = useMemo(() => {
    if (parseFloat(maxGlobalGasUsd) < 0.01) {
      return minGasError;
    }
    if (isPerUserLimitEnabled && parseFloat(maxGasPerAddrUsd) > parseFloat(maxGlobalGasUsd)) {
      return maxGasErrorMessage;
    }
    if (isPerUserOpLimitEnabled && parseFloat(maxGasPerUserOpUsd) > parseFloat(maxGlobalGasUsd)) {
      return maxUserOpGasErrorMessage;
    }
    return '';
  }, [
    isPerUserLimitEnabled,
    isPerUserOpLimitEnabled,
    maxGasPerAddrUsd,
    maxGasPerUserOpUsd,
    maxGlobalGasUsd,
  ]);

  const userMaxGasError = useMemo(() => {
    if (isPerUserLimitEnabled && parseFloat(maxGasPerAddrUsd) < 0.01) {
      return minGasError;
    }
    if (isPerUserLimitEnabled && parseFloat(maxGlobalGasUsd) < parseFloat(maxGasPerAddrUsd)) {
      return maxGasErrorMessage;
    }
    return '';
  }, [isPerUserLimitEnabled, maxGasPerAddrUsd, maxGlobalGasUsd]);

  const userMaxOpError = useMemo(() => {
    if (isPerUserLimitEnabled && parseFloat(maxTxnPerAddr) < 1) {
      return minUserOpError;
    }
    return '';
  }, [isPerUserLimitEnabled, maxTxnPerAddr]);

  const userOpMaxGasError = useMemo(() => {
    if (isPerUserOpLimitEnabled && parseFloat(maxGasPerUserOpUsd) < 0.01) {
      return minGasError;
    }
    if (isPerUserOpLimitEnabled && parseFloat(maxGlobalGasUsd) < parseFloat(maxGasPerUserOpUsd)) {
      return maxUserOpGasErrorMessage;
    }
    return '';
  }, [isPerUserOpLimitEnabled, maxGasPerUserOpUsd, maxGlobalGasUsd]);

  const numChangesUnsaved = useMemo(() => {
    let count = 0;
    if (gasPolicy?.maxGasPerUserOperationUsd !== parseFloat(maxGasPerUserOpUsd || '0')) {
      count += 1;
    }
    if (gasPolicy?.maxGasPerAddrUsd !== parseFloat(maxGasPerAddrUsd)) {
      count += 1;
    }
    if (gasPolicy?.maxTxnPerAddr !== maxTxnPerAddr) {
      count += 1;
    }
    if (
      gasPolicy?.intervalType &&
      getKeyByValue(LIMIT_CYCLE_MAP, gasPolicy?.intervalType) !== perAddressLimitCycle
    ) {
      count += 1;
    }
    if (gasPolicy?.maxGlobalGasUsd !== parseFloat(maxGlobalGasUsd)) {
      count += 1;
    }
    if (gasPolicy?.maxGasPerUserOperationEnabled !== isPerUserOpLimitEnabled) {
      count += 1;
    }
    if (gasPolicy?.perAddrMaxEnabled !== isPerUserLimitEnabled) {
      count += 1;
    }
    return count;
  }, [
    gasPolicy?.intervalType,
    gasPolicy?.maxGasPerAddrUsd,
    gasPolicy?.maxGasPerUserOperationEnabled,
    gasPolicy?.maxGasPerUserOperationUsd,
    gasPolicy?.maxGlobalGasUsd,
    gasPolicy?.maxTxnPerAddr,
    gasPolicy?.perAddrMaxEnabled,
    isPerUserLimitEnabled,
    isPerUserOpLimitEnabled,
    maxGasPerAddrUsd,
    maxGasPerUserOpUsd,
    maxGlobalGasUsd,
    maxTxnPerAddr,
    perAddressLimitCycle,
  ]);

  const handleMaxGlobalGasUsdChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (isValidGasAmount(event.target.value)) {
      setMaxGlobalGasUsd(event.target.value);
    }
  }, []);

  const handleMaxGasPerAddressChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (isValidGasAmount(event.target.value)) {
      setMaxGasPerAddrUsd(event.target.value);
    }
  }, []);

  const handleMaxGasPerUserOpChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (isValidGasAmount(event.target.value)) {
      setMaxGasPerUserOpUsd(event.target.value);
    }
  }, []);

  const handleMaxTxnPerAddressChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (isValidTxnAmount(event.target.value)) {
      setMaxTxnPerAddr(event.target.value);
    }
  }, []);

  const handleTogglePerUserMax = useCallback(() => {
    setIsPerUserLimitEnabled(!isPerUserLimitEnabled);
  }, [isPerUserLimitEnabled]);

  const handleTogglePerUserOpMax = useCallback(() => {
    setIsPerUserOpLimitEnabled(!isPerUserOpLimitEnabled);
  }, [isPerUserOpLimitEnabled]);

  const handleSavePress = useCallback(() => {
    if (!gasPolicy) {
      return;
    }

    const perAddrMax =
      parseFloat(maxGlobalGasUsd) < parseFloat(maxGasPerAddrUsd)
        ? parseFloat(maxGlobalGasUsd)
        : parseFloat(maxGasPerAddrUsd);

    const perUserOpMax =
      parseFloat(maxGasPerUserOpUsd) > parseFloat(maxGlobalGasUsd)
        ? parseFloat(maxGlobalGasUsd)
        : parseFloat(maxGasPerUserOpUsd);

    const newGasPolicy = {
      ...gasPolicy,
      maxGasPerUserOperationUsd: perUserOpMax,
      maxGlobalGasUsd: parseFloat(maxGlobalGasUsd),
      maxGasPerAddrUsd: perAddrMax,
      maxTxnPerAddr: formatTxnAmount(maxTxnPerAddr),
      perAddrMaxEnabled: isPerUserLimitEnabled,
      maxGasPerUserOperationEnabled: isPerUserOpLimitEnabled,
      intervalType: perAddressLimitCycle
        ? LIMIT_CYCLE_MAP[perAddressLimitCycle]
        : gasPolicy?.intervalType,
    };
    updateGasPolicy.mutate({ policy: newGasPolicy });
    logClick(EventName.paymaster_update_global_gas_limit);
  }, [
    gasPolicy,
    isPerUserLimitEnabled,
    isPerUserOpLimitEnabled,
    maxGasPerAddrUsd,
    maxGasPerUserOpUsd,
    maxGlobalGasUsd,
    maxTxnPerAddr,
    perAddressLimitCycle,
    updateGasPolicy,
  ]);

  const isError =
    !!globalMaxGasError || !!userMaxGasError || !!userOpMaxGasError || !!userMaxOpError;
  const hasInvalidInputs =
    !maxGasPerUserOpUsd ||
    maxGasPerUserOpUsd === '.' ||
    !maxGasPerAddrUsd ||
    maxGasPerAddrUsd === '.' ||
    !maxGlobalGasUsd ||
    maxGlobalGasUsd === '.';

  const isSaveDisabled = !numChangesUnsaved || isError || hasInvalidInputs;

  return (
    <VStack bordered borderRadius="rounded" spacing={4} gap={3} testID="paymaster-limit-section">
      <TextTitle3 as="h3">Gas policy</TextTitle3>
      {isDisabled && !isApproachingGasLimit && <ContractAllowlistBanner spacingTop={1} />}
      {isApproachingGasLimit && (
        <PaymasterGasBanner spacingTop={1} percentageUsed={progressBarProgress} />
      )}

      <VStack gap={3} spacingTop={4}>
        <PaymasterLimitHeader
          iconName="globe"
          title="Global limit"
          description="Set the maximum amount you are willing to sponsor globally."
        />
        <Box spacing={4} bordered borderRadius="rounded" maxWidth={464}>
          <VStack gap={2} width="100%">
            <TextLabel1 as="p">Sponsored gas globally</TextLabel1>
            <HStack alignItems="flex-end">
              <TextDisplay3 as="h3">{formattedSponsoredGas}</TextDisplay3>
              <TextBody as="p" color="foregroundMuted">{`/ ${formattedMaxGlobalGas}`}</TextBody>
            </HStack>
            <ProgressBar progress={progressBarProgress > 1 ? 1 : progressBarProgress} />
          </VStack>
        </Box>
        <TextInput
          type="text"
          label="Maximum USD"
          placeholder="0.00"
          helperText={globalMaxGasError || 'Max amount of USD this policy will sponsor globally'}
          value={maxGlobalGasUsd}
          onChange={handleMaxGlobalGasUsdChange}
          suffix="USD"
          variant={globalMaxGasError ? 'negative' : 'foregroundMuted'}
        />
      </VStack>

      <VStack gap={3} spacingTop={6}>
        <PaymasterLimitHeader
          iconName="profile"
          title="Per user limit"
          description="Set the maximum amount you are willing to sponsor per user."
          actionElement={
            <Switch
              onChange={handleTogglePerUserMax}
              checked={isPerUserLimitEnabled}
              disabled={updateGasPolicy?.isLoading}
            />
          }
        />
        {isPerUserLimitEnabled && (
          <VStack>
            <Grid
              spacingTop={isDisabled ? 0 : 4}
              gap={1}
              templateColumns={responsiveTemplateColumns}
              spacingBottom={2}
            >
              <TextInput
                type="text"
                label="Maximum USD"
                placeholder="0.00"
                helperText={
                  userMaxGasError || 'Max amount of USD this policy will sponsor per user'
                }
                value={maxGasPerAddrUsd}
                onChange={handleMaxGasPerAddressChange}
                suffix="USD"
                variant={userMaxGasError ? 'negative' : 'foregroundMuted'}
              />
              <TextInput
                type="text"
                label="Maximum Number of User Operations"
                placeholder="0"
                helperText={
                  userMaxOpError ||
                  'Max number of User Operations this policy will sponsor per user'
                }
                value={maxTxnPerAddr}
                onChange={handleMaxTxnPerAddressChange}
                variant={userMaxOpError ? 'negative' : 'foregroundMuted'}
              />
            </Grid>
            <Select
              value={perAddressLimitCycle}
              onChange={setPerAddressLimitCycle}
              label="Limit cycle"
              helperText="The per user limits will reset every cycle, starting from when the change is saved"
            >
              {Object.keys(LIMIT_CYCLE_MAP).map((option) => (
                <SelectOption value={option} key={option} title={option} />
              ))}
            </Select>
          </VStack>
        )}
      </VStack>

      <VStack gap={3} spacingTop={6}>
        <PaymasterLimitHeader
          iconName="transactions"
          title="Per User Operation limit"
          description="Set the maximum amount you are willing to sponsor per User Operation."
          actionElement={
            <Switch
              onChange={handleTogglePerUserOpMax}
              checked={isPerUserOpLimitEnabled}
              disabled={updateGasPolicy?.isLoading}
            />
          }
        />

        {isPerUserOpLimitEnabled && (
          <TextInput
            type="text"
            label="Maximum USD"
            placeholder="0.00"
            helperText={
              userOpMaxGasError || 'Max amount of USD this policy will sponsor per User Operation'
            }
            value={maxGasPerUserOpUsd}
            onChange={handleMaxGasPerUserOpChange}
            suffix="USD"
            variant={userOpMaxGasError ? 'negative' : 'foregroundMuted'}
          />
        )}
      </VStack>

      <HStack gap={2} justifyContent="flex-end" alignItems="center" spacingTop={7}>
        <TextBody as="p" color="foregroundMuted">{`${numChangesUnsaved} unsaved change${
          numChangesUnsaved === 1 ? '' : 's'
        }`}</TextBody>
        <Button variant="primary" onPress={handleSavePress} disabled={isSaveDisabled}>
          Save
        </Button>
      </HStack>
    </VStack>
  );
}
